import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Navbar, Footer, Reveal, ProductImage, ease } from "@/components/shared";
import { site, categorie } from "@/config/site";
import { api, euro } from "@/lib/api";
import { publicUrl } from "@/lib/publicUrl";
import NotFound from "@/pages/NotFound";

const HEADERS = {
  bare: publicUrl("/catalog/bare/rovere.png"),
  urne: publicUrl("/catalog/urne/marmo.png"),
  lapidi: publicUrl("/catalog/lapidi/carrara.png"),
  fiori: publicUrl("/catalog/fiori/cuscino-rose.png"),
  carri: publicUrl("/catalog/carri/mercedes.png"),
  accessori: publicUrl("/catalog/accessori/manifesti.png"),
  pratiche: publicUrl("/catalog/pratiche/complete.png"),
};

export default function CategoryPage() {
  const { categoria } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const meta = categorie[categoria];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!categorie[categoria]) return;
    setLoading(true);
    setError(false);
    api.catalog(categoria).then(setItems).catch(() => { setItems([]); setError(true); }).finally(() => setLoading(false));
  }, [categoria]);

  if (!meta) return <NotFound />;

  return (
    <div data-testid="category-page">
      <Navbar />
      <section className="relative min-h-[48vh] flex items-end overflow-hidden pt-header">
        <motion.div className="absolute inset-0" initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease }}>
          <img src={HEADERS[categoria]} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/60 to-[#0A192F]/30" />
        </motion.div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-14 w-full">
          <Link to="/#catalogo" className="inline-flex items-center gap-2 text-white/70 text-sm hover:text-white mb-4"><ArrowLeft size={16} /> Catalogo</Link>
          <p className="overline mb-3">Prodotti e servizi</p>
          <h1 className="text-white font-serif font-light text-5xl md:text-6xl">{meta.label}</h1>
          <p className="mt-4 text-white/80 text-lg max-w-xl">{meta.desc}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading && [...Array(6)].map((_, i) => (
            <div key={i} className="rounded-xl border border-border bg-card overflow-hidden animate-pulse">
              <div className="aspect-[4/3] bg-secondary" />
              <div className="p-6 space-y-2"><div className="h-5 bg-secondary rounded w-1/2" /><div className="h-3 bg-secondary rounded w-full" /></div>
            </div>
          ))}
          {!loading && error && (
            <p className="col-span-full text-muted-foreground">
              Catalogo non disponibile. Ci chiami al <a href={`tel:${site.telefonoRaw}`} className="text-primary font-semibold">{site.telefono}</a>.
            </p>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="col-span-full text-muted-foreground">Nessun prodotto in questa categoria.</p>
          )}
          {!loading && items.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.05}>
              <div className="bg-card border border-border rounded-xl overflow-hidden h-full flex flex-col" data-testid={`svc-item-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-secondary">
                  <ProductImage src={p.immagine} alt={p.nome} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-2xl font-serif text-primary">{p.nome}</h3>
                  {p.materiale && <p className="text-xs text-muted-foreground mt-1">{p.materiale}{p.finitura ? ` · ${p.finitura}` : ""}</p>}
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{p.descrizione}</p>
                  {p.varianti?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {p.varianti.map((v) => <span key={v} className="text-xs px-3 py-1 rounded-full bg-secondary text-primary">{v}</span>)}
                    </div>
                  )}
                  {p.opzioni?.length > 0 && (
                    <ul className="mt-4 space-y-1">
                      {p.opzioni.map((o) => (
                        <li key={o.nome} className="text-xs text-muted-foreground flex items-center gap-2"><Check size={13} className="text-accent" /> {o.nome} (+{euro(o.prezzo)})</li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-auto pt-5 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">A partire da</p>
                      <p className="text-xl font-serif text-primary">{euro(p.prezzo)}</p>
                    </div>
                    <Link to={`/preventivo?prodotto=${p.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary border-b-2 border-accent pb-0.5 hover:gap-2.5 transition-all">
                      Al preventivo <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 bg-primary text-primary-foreground rounded-2xl p-10 md:p-14 text-center grain relative overflow-hidden">
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-serif font-light">Componga il preventivo completo</h2>
            <p className="mt-3 text-white/70 max-w-lg mx-auto">Il configuratore le permette di scegliere ogni servizio e ottenere il totale in tempo reale.</p>
            <Link to="/preventivo" className="mt-8 inline-flex items-center gap-2 bg-accent text-primary font-semibold px-8 py-4 rounded-full hover:-translate-y-0.5 transition-all">
              Vai al preventivatore <ArrowRight size={18} />
            </Link>
            <p className="mt-6 text-sm text-white/60">Oppure chiamaci al <a href={`tel:${site.telefonoRaw}`} className="text-accent font-semibold">{site.telefono}</a></p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
